import { useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { insertTrackEvent } from '@comeon/analytics';
import { H4Bold, Spinner } from '@comeon/ui';
import SidebarHeader from '~/components/header/sidebar-header';
import { SmallerContainer } from '~/components/layout/mediaquery';
import useTranslations from '~/components/translation/use-translations.ts';
import IconChevronLeftButton from '~/components/misc/icon-chevron-left-button';
import ConfirmationFailure from '~/components/modal/confirmation-failure';
import { useTheme } from '~/components/theme/theme-context';
import useRouterCallbacks from '~/hooks/use-router-callbacks';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer';
import { useRouter } from '~/utils/navigation';
import usePrevious from '~/hooks/use-previous';
import queryString from 'query-string';

const StyledIframe = styled.iframe`
	max-height: ${props => (props.fillHeight ? '100%' : 'calc(100% - 48px)')};
`;

const textKeys = ['API_ERROR_TITLE', 'OK', 'SYSTEM_ERROR_GENERAL'];

const ExternalIframeRequest = ({ hideHeader, fillHeight, onClose, fullLocation, setResultParams }) => {
	const [redirectUrl, setRedirectUrl] = useState(null);
	const [showloader, setLoader] = useState(true);
	const iframeRef = useRef();
	const { Logo } = useTheme();
	const { transformPathAndPush, pushBack, basePath } = useRouterCallbacks();
	const iframeExternalRequestUrl = useSelector(state => state.brand.iframeExternalRequestUrl);
	const iframeExternalParams = useSelector(state => state.brand.iframeExternalParams);
	const sendExternalParams = useSelector(state => state.brand.sendExternalParams);
	const exitFlowTo = useSelector(state => state.authentication?.exitPath);
	const transformPath = useContextualPathTransformer();
	const router = useRouter();
	const prevRedirectUrl = usePrevious(redirectUrl);
	const { translations, isLoadingTranslations } = useTranslations(textKeys);

	const postFormUsingIframe = iframeExternalParams?.method;

	const postFormInIframe = () => {
		if (iframeExternalRequestUrl?.includes('trustly')) {
			insertTrackEvent({
				category: 'Deposit',
				action: 'Trustly - Deposit Details',
				label: `iframeExternalRequestUrl: ${iframeExternalRequestUrl} -
                sendExternalParams: ${sendExternalParams}  -
                iframeExternalParams: ${JSON.stringify(iframeExternalParams)}`
			});
		}

		if (sendExternalParams || postFormUsingIframe === 'POST') {
			const form = document.createElement('form');
			form.setAttribute('method', postFormUsingIframe || 'GET');
			form.setAttribute('action', iframeExternalRequestUrl);
			form.setAttribute('target', 'payment-Iframe');
			form.setAttribute('id', 'paymentIframe');
			Object.entries(iframeExternalParams).forEach(([key, value]) => {
				const hiddenField = document.createElement('input');
				hiddenField.setAttribute('type', 'hidden');
				hiddenField.setAttribute('name', key);
				hiddenField.setAttribute('value', value);
				form.appendChild(hiddenField);
			});
			iframeRef.current?.appendChild(form);
			form?.submit();
		}
	};

	useEffect(() => {
		postFormInIframe();

		const receiveMessage = event => {
			const { IframeRedirectUrl } = event.data;
			if (window.location.origin === event.origin && IframeRedirectUrl) {
				setRedirectUrl(IframeRedirectUrl);
			}
		};
		window.addEventListener('message', receiveMessage, false);

		// cleanupListener
		return () => window.removeEventListener('message', receiveMessage, false);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [iframeExternalRequestUrl]);

	const handleQDRedirect = () => {
		const parsedQuery = queryString.parse(redirectUrl.split('?')[1]);
		onClose();
		return setResultParams(parsedQuery);
	};

	// TODO: this is a hack, we get multiple redirect url from post message which breaks it
	// can we only run it once??
	useEffect(() => {
		// only run it once
		if (redirectUrl && prevRedirectUrl === null) {
			if (onClose && setResultParams) {
				handleQDRedirect();
			}
			const path = transformPath(redirectUrl, {
				location: fullLocation ?? window.location
			});
			router.push(path);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [redirectUrl, transformPath, router.push, fullLocation, prevRedirectUrl]);

	const onConfirmGeneralError = () => {
		if (onClose) {
			onClose();
		} else {
			const path = exitFlowTo || basePath;
			transformPathAndPush(path);
		}
	};

	if (!iframeExternalRequestUrl && !isLoadingTranslations) {
		return (
			<ConfirmationFailure
				title={translations.API_ERROR_TITLE}
				confirmButtonText={translations.OK}
				onConfirm={onConfirmGeneralError}
				text={translations.SYSTEM_ERROR_GENERAL}
				onClick={onConfirmGeneralError}
			/>
		);
	}

	return (
		<>
			{!hideHeader ? (
				<SidebarHeader
					transparent
					className="external-iframe"
					Icon={
						iframeExternalParams?.backButton && (
							<IconChevronLeftButton onClick={pushBack} className="js-back page-head__back u-show" />
						)
					}
				>
					<div className="page-head__title svg-logo--dark" data-at="logo-header-external-iframe-request">
						<Logo />
					</div>
					<div className="page-head__right-side" />
				</SidebarHeader>
			) : null}

			{iframeExternalRequestUrl === 'pending' ? (
				<Spinner />
			) : (
				<>
					<SmallerContainer>
						<H4Bold textAlign="center" />
					</SmallerContainer>
					<StyledIframe
						title="payment-frame"
						name="payment-Iframe"
						id="paymentIframe"
						fillHeight={fillHeight}
						onLoad={() => setLoader(false)}
						className={`iframe-${iframeExternalParams?.solidBackground}-bg`}
						src={postFormUsingIframe === 'POST' || sendExternalParams ? null : iframeExternalRequestUrl}
						ref={iframeRef}
						height="100%"
					>
						Loading...
					</StyledIframe>
					{showloader ? <Spinner /> : null}
				</>
			)}
		</>
	);
};

export default ExternalIframeRequest;
